import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';

import {cn} from '@/modules/foundation/shared/tailwind';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
	/** Heading level. This will translate into any h1-6 level unless `asChild` is defined */
	level?: 1 | 2 | 3 | 4 | 5 | 6;
	/**
	 * Changes text size. See options below:
	 * @default '3xl'
	 */
	size?: Size;
	/** Adds margin-bottom */
	spacing?: boolean;
	/**
	 * Change the default rendered element for the one passed as a child, merging their props and behavior.
	 * @default false
	 */
	asChild?: boolean;
};
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
	({level = 1, size, spacing = false, className, asChild, ...rest}, ref) => {
		const Component = asChild ? Slot : `h${level ?? 1}`;

		let defaultSize: HeadingProps['size'] = '3xl';
		switch (level) {
			case 1:
				defaultSize = '3xl';
				break;
			case 2:
				defaultSize = '2xl';
				break;
			case 3:
				defaultSize = 'xl';
				break;
			case 4:
				defaultSize = 'lg';
				break;
			case 5:
				defaultSize = 'md';
				break;
			case 6:
				defaultSize = 'sm';
				break;
		}

		size = size ?? defaultSize;

		return (
			<Component
				ref={ref}
				className={cn([
					'font-display',
					'font-medium',
					size === 'xs' && ['text-[15px]', 'leading-tight'],
					size === 'sm' && ['text-[17px]', 'leading-tight'],
					size === 'md' && [
						'text-[17px]',
						'md:text-[21px]',
						'leading-tight',
					],
					size === 'lg' && ['text-[27px]', 'leading-tight'],
					size === 'xl' && ['text-[31px]', 'leading-tight'],
					size === '2xl' && [
						'text-[31px]',
						'md:text-[44px]',
						'leading-tight',
					],
					size === '3xl' && [
						'text-[44px]',
						'md:text-[63px]',
						'leading-tight',
					],
					spacing && [
						size === 'xs' && ['mb-2', 'last:mb-0'],
						size === 'sm' && ['mb-2', 'last:mb-0'],
						size === 'md' && ['mb-3', 'last:mb-0'],
						size === 'lg' && ['mb-3', 'last:mb-0'],
						size === 'xl' && ['mb-3', 'last:mb-0'],
						size === '2xl' && ['mb-3', 'last:mb-0'],
						size === '3xl' && ['mb-7', 'last:mb-0'],
					],
					className,
				])}
				{...rest}
			/>
		);
	},
);

Heading.displayName = 'Heading';
